import {
  SELF_CERTIFICATION_DOCUMENT_TO_TAX_FORM_TYPE,
  W_SERIES_DOCUMENT_TO_TAX_FORM_TYPE,
} from "../constants";
import { type TaxFormType } from "../types";

/**
 * Document category types for tax documents.
 * - wSeries: W-9, W-8BEN, W-8BEN-E, W-8ECI, W-8EXP, W-8IMY, Withholding Statement
 * - selfCertification: CRS Individual, CRS Entity
 * - none: Not a tax document
 */
export type TaxDocumentCategory = "wSeries" | "selfCertification" | "none";

/**
 * Base types for tax documents as stored in documentPurpose.baseType
 */
export const W_SERIES_BASE_TYPE = "w_series_tax_form";
export const SELF_CERTIFICATION_BASE_TYPE = "self_certification";
export const TAX_DOCUMENT_BASE_TYPES = [
  W_SERIES_BASE_TYPE,
  SELF_CERTIFICATION_BASE_TYPE,
] as const;

/**
 * Human-readable labels for W-series document types
 */
export const W_SERIES_LABELS: Record<string, string> = {
  w9: "W-9",
  "W-9": "W-9",
  w8ben: "W-8BEN",
  "W-8BEN": "W-8BEN",
  w8bene: "W-8BEN-E",
  "W-8BEN-E": "W-8BEN-E",
  w8eci: "W-8ECI",
  "W-8ECI": "W-8ECI",
  w8exp: "W-8EXP",
  "W-8EXP": "W-8EXP",
  w8imy: "W-8IMY",
  "W-8IMY": "W-8IMY",
  withholding_statement: "Withholding Statement",
};

/**
 * Human-readable labels for self-certification document types
 */
export const SELF_CERT_LABELS: Record<string, string> = {
  individual_self_certification: "Individual Self Certification",
  entity_self_certification: "Entity Self Certification",
};

/**
 * Category labels for display
 */
export const CATEGORY_LABELS: Record<TaxDocumentCategory, string> = {
  wSeries: "FATCA",
  selfCertification: "CRS",
  none: "",
};

/**
 * Determines the document category based on the baseType from documentPurpose.
 */
export const getDocumentCategory = (
  baseType: string | null | undefined,
): TaxDocumentCategory => {
  if (baseType === W_SERIES_BASE_TYPE) return "wSeries";
  if (baseType === SELF_CERTIFICATION_BASE_TYPE) return "selfCertification";
  return "none";
};

/**
 * Extracts the secondary document type (subtype) from document metadata.
 */
export const getSecondaryDocumentType = (
  metadata: { key: string; value?: string | null }[] | null | undefined,
): string | undefined => {
  return metadata?.find((m) => m.key === "type")?.value ?? undefined;
};

/**
 * Determines the tax form type (for routing/config lookup) based on document category and subtype.
 */
export const getTaxFormTypeFromDocument = (
  category: TaxDocumentCategory,
  secondaryDocumentType: string | undefined,
): TaxFormType | undefined => {
  if (!secondaryDocumentType) return undefined;

  if (category === "wSeries") {
    return W_SERIES_DOCUMENT_TO_TAX_FORM_TYPE[secondaryDocumentType];
  }
  if (category === "selfCertification") {
    return SELF_CERTIFICATION_DOCUMENT_TO_TAX_FORM_TYPE[secondaryDocumentType];
  }
  return undefined;
};

/**
 * Gets the human-readable label for a document subtype (e.g., "W-8BEN", "Individual Self-Certification").
 */
export const getFormTypeLabel = (
  category: TaxDocumentCategory,
  secondaryDocumentType: string | undefined,
): string => {
  if (!secondaryDocumentType) return "Unknown";

  if (category === "wSeries") {
    return W_SERIES_LABELS[secondaryDocumentType] ?? secondaryDocumentType;
  }
  if (category === "selfCertification") {
    return SELF_CERT_LABELS[secondaryDocumentType] ?? "Self Certification";
  }
  return secondaryDocumentType;
};

/**
 * Gets the category label (FATCA or CRS) for a document category.
 */
export const getCategoryLabel = (category: TaxDocumentCategory): string => {
  return CATEGORY_LABELS[category];
};
